import { z } from 'zod';
export const planProposalChatCardSchema = z.strictObject({
  type: z.literal('PLAN_PROPOSAL'),
  proposalId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((id) => id[14] === '7'),
  proposalNo: z.number().int().min(1).max(2147483647),
  optionCount: z.number().int().min(1).max(3),
});
export type PlanProposalChatCard = z.infer<typeof planProposalChatCardSchema>;
/** Native PG instants have up to six fractional digits; retain the original send time exactly. */
export function chatCardInstant(value: string) {
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(
      value,
    ) ||
    !Number.isFinite(Date.parse(value))
  )
    throw new Error('PLAN_CARD_INVALID');
  const fraction = value.match(/\.(\d{1,6})(?:Z|[+-]\d{2}:\d{2})$/)?.[1] ?? '';
  return (
    BigInt(Date.parse(value)) * 1000n + BigInt(fraction.padEnd(6, '0').slice(3))
  );
}

export const chatFormCodeSchema = z.enum([
  'TRAVEL_DATES',
  'SKIN_CONCERNS',
  'BUDGET_RANGE',
]);
export const formRequestChatCardSchema = z.strictObject({
  type: z.literal('FORM_REQUEST'),
  requestId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((value) => value[14] === '7'),
  formCode: chatFormCodeSchema,
});
export type FormRequestChatCard = z.infer<typeof formRequestChatCardSchema>;

export const paymentLinkChatCardSchema = z.strictObject({
  type: z.literal('PAYMENT_LINK'),
  orderId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((value) => value[14] === '7'),
  orderNo: z
    .string()
    .max(40)
    .regex(/^GO-[0-9]{8}-[1-9][0-9]{0,18}$/),
});
export type PaymentLinkChatCard = z.infer<typeof paymentLinkChatCardSchema>;

export const bookingChatCardSchema = z
  .strictObject({
    type: z.literal('BOOKING'),
    bookingId: z
      .uuid()
      .regex(/^[0-9a-f-]{36}$/)
      .refine((v) => v[14] === '7'),
    orderNo: z
      .string()
      .max(40)
      .regex(/^GO-[0-9]{8}-[1-9][0-9]{0,18}$/),
    bookingVersion: z.number().int().min(0).max(2147483647),
    status: z.enum([
      'REQUESTED',
      'CONFIRMED',
      'VISITED',
      'COMPLETED',
      'CANCELED',
      'REPLACED',
    ]),
  })
  .refine((v) =>
    v.status === 'REPLACED'
      ? v.bookingVersion === 1
      : v.status === 'REQUESTED'
        ? v.bookingVersion === 0
        : v.status === 'CONFIRMED'
          ? v.bookingVersion >= 1
          : v.status === 'VISITED'
            ? v.bookingVersion >= 2
            : v.status === 'COMPLETED'
              ? v.bookingVersion >= 3
              : v.bookingVersion >= 1,
  );
export type BookingChatCard = z.infer<typeof bookingChatCardSchema>;

export const priceChatCardSchema = z
  .strictObject({
    type: z.literal('PRICE_GUARANTEE'),
    voucherId: z
      .uuid()
      .regex(/^[0-9a-f-]{36}$/)
      .refine((v) => v[14] === '7'),
    voucherNo: z
      .string()
      .max(40)
      .regex(/^GV-[0-9]{8}-[1-9][0-9]{0,18}$/),
    protectedFrom: z.iso.datetime({ offset: true }),
    protectedUntil: z.iso.datetime({ offset: true }),
  })
  .refine(
    (v) => chatCardInstant(v.protectedFrom) < chatCardInstant(v.protectedUntil),
  );
export type PriceChatCard = z.infer<typeof priceChatCardSchema>;

export const policyChatCardSchema = z.strictObject({
  type: z.literal('POLICY_SUMMARY'),
  orderId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  orderNo: z
    .string()
    .max(40)
    .regex(/^GO-[0-9]{8}-[1-9][0-9]{0,18}$/),
  policyCount: z.literal(3),
});
export type PolicyChatCard = z.infer<typeof policyChatCardSchema>;

export const aftercareChatCardSchema = z.strictObject({
  type: z.literal('AFTERCARE_CHECKIN'),
  bookingId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  planId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  checkinId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  dayOffset: z.union([z.literal(1), z.literal(3), z.literal(7), z.literal(30)]),
  dueAt: z.iso.datetime({ offset: true }),
});
export type AftercareChatCard = z.infer<typeof aftercareChatCardSchema>;

export const checklistChatCardSchema = z.strictObject({
  type: z.literal('CHECKLIST'),
  bookingId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  confirmationVersion: z.number().int().min(1).max(2147483647),
  definitionVersion: z.literal(1),
});
export type ChecklistChatCard = z.infer<typeof checklistChatCardSchema>;

export const voucherOfferChatCardSchema = z.strictObject({
  type: z.literal('VOUCHER_OFFER'),
  proposalId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  proposalNo: z.number().int().min(1).max(2147483647),
  optionId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  position: z.number().int().min(1).max(3),
  productType: z.enum(['CARE_DEPOSIT', 'JOURNEY_VOUCHER']),
  proposalSentAt: z.iso.datetime({ offset: true }),
});
export type VoucherOfferChatCard = z.infer<typeof voucherOfferChatCardSchema>;

/** Native standalone membership purchase, never a PurchaseOrder or an invented order number. */
export const membershipPaymentLinkChatCardSchema = z.strictObject({
  type: z.literal('PAYMENT_LINK'),
  targetKind: z.literal('MEMBERSHIP_PURCHASE'),
  purchaseId: z
    .uuid()
    .regex(/^[0-9a-f-]{36}$/)
    .refine((v) => v[14] === '7'),
  planCode: z.enum(['GLOW_CARE', 'GLOW_CONCIERGE']),
});
export type MembershipPaymentLinkChatCard = z.infer<
  typeof membershipPaymentLinkChatCardSchema
>;
